<script>
import TopBar from './components/nav-menu/TopBar'
import SideBar from './components/nav-menu/SideBar'
import Logout from './components/Logout'
import axios from './axiosClient'
import ModalWrapper from './components/modal/Wrapper'
import LayoutManager from './components/layout-manager/LayoutManager'
import LangSwitcher from './components/nav-menu/LangSwitcher.vue'
import ScrollToTop from './components/ScrollToTop'
import TemplateViewer from './components/list-view/TemplateViewer.vue'

import cssLoader from './utils/mixin-css-loader'
import core from './utils/mixin-core'
import { isNull, isEmpty, get, set, isEqual } from 'lodash'
import md5 from 'crypto-js/md5'

const REACT_NATIVE_USER_AGENT = 'React-Native'
const NATIVESCRIPT_USER_AGENT = 'Nativescript'
const NATIVESCRIPT_BODY_CLASS = 'ns-webview'

/**
 * Merges fix system components with dynamic components used by LayoutLoader
 * @returns { components }
 */
function getComponents() {
  const componentList = require('./components/custom-components/custom-component-list').componentList
  const dynamicComponents = {}
  componentList.map((component) => {
    dynamicComponents[component] = require(`./components/custom-components/${component}`)['default']
  })
  return Object.assign(
    {},
    {
      ModalWrapper,
      Logout,
      SideBar,
      TopBar,
      LayoutManager,
      LangSwitcher,
      ScrollToTop,
    },
    dynamicComponents,
  )
}

/**
 * @class LayoutLoader
 *
 * @description main component for Application
 */
export default {
  name: 'layout-loader',
  mixins: [cssLoader, core],
  components: getComponents(),
  data() {
    return {
      layout: '',
    }
  },
  render(h) {
    const that = this
    const singleLayoutRenderingId = that.getSingleLayoutRenderingId()

    if (singleLayoutRenderingId) {
      return h(TemplateViewer, {
        props: {
          template: { id: singleLayoutRenderingId },
        },
      })
    }

    return h(
      {
        name: 'rendered-layout-loader',
        mixins: [core],
        components: getComponents(),
        methods: {},
        template: `<div>${that.layout}</div>`,
      },
      { props: {} },
    )
  },
  methods: {
    async getLayout() {
      const response = await axios().get('/api/Layouts/get-layout', {
        params: {
          url: this.$CORE.url,
        },
      })

      const layout = get(response, 'data.layout', {})

      return {
        html: layout.htmlContentVue,
        css: layout.cssContent,
        js: layout.jsContent,
        seo: {
          title: layout.seoTitle,
          description: layout.seoDescription,
          keywords: layout.seoKeywords,
        },
        social: {
          title: layout.socialTitle,
          description: layout.socialDescription,
          image: layout.socialImage,
        },
      }
    },
    /**
     * Checks if content is not empty
     * @param content
     * @returns {boolean}
     */
    hasContent(content) {
      return !isNull(content) && !isEmpty(content)
    },
    /**
     * Creates <style> tag in the document <head> containing css
     * @param content
     * @returns {Promise<void>}
     */
    setStyle(content) {
      const cssContentHash = md5(content).toString()
      if (get(window.CORE, ['alreadyLoadedInHeader', 'styles', cssContentHash])) return
      const style = document.createElement('style')
      style.setAttribute('style', 'text/css')
      style.appendChild(document.createTextNode(content))
      document.head.appendChild(style)
      set(window.CORE, ['alreadyLoadedInHeader', 'styles', cssContentHash], true)
    },
    checkUserAgent() {
      if (
        isEqual(navigator.userAgent, NATIVESCRIPT_USER_AGENT) ||
        isEqual(navigator.userAgent, REACT_NATIVE_USER_AGENT)
      ) {
        document.querySelector('body').classList.add(NATIVESCRIPT_BODY_CLASS)
      }
    },
    /**
     * Creates <script> tag in the document <head> containing javascript content
     * @param content
     * @returns {Promise<void>}
     */
    async setScript(content = '') {
      const jsContentHash = md5(content).toString()
      if (get(window.CORE, ['alreadyLoadedInHeader', 'scripts', jsContentHash])) return
      if (content.match('jQuery')) {
        await this.useJQuery()
      }
      const script = document.createElement('script')
      script.setAttribute('type', 'text/javascript')
      script.appendChild(document.createTextNode(content))
      document.head.appendChild(script)
      set(window.CORE, ['alreadyLoadedInHeader', 'scripts', jsContentHash], true)
    },
    /**
     * Imports jQuery into a <script> tag into the document <head>
     * @returns {Promise<void>}
     */
    async useJQuery() {
      const jQuery = document.createElement('script')
      const code = await axios().get('https://ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js')
      jQuery.setAttribute('type', 'text/javascript')
      jQuery.appendChild(document.createTextNode(code.data))
      document.head.appendChild(jQuery)
    },
    setMetas(layout) {
      if (get(layout, 'seo.title')) {
        this.setMeta('title', layout.seo.title)
      }
      if (get(layout, 'seo.description')) {
        this.setMeta('description', layout.seo.description)
      }
      if (get(layout, 'seo.keywords')) {
        this.setMeta('keywords', layout.seo.keywords)
      }
      if (get(layout, 'social.title')) {
        this.setMeta('og:title', layout.social.title)
      }
      if (get(layout, 'social.description')) {
        this.setMeta('og:description', layout.social.description)
      }
      if (get(layout, 'social.image')) {
        this.setMeta('og:image', layout.social.image)
      }
    },
    setMeta(name, content) {
      let meta = document.createElement('meta')
      meta.name = name
      meta.content = content
      document.head.appendChild(meta)
    },
    redirectByEvent(data) {
      document.location.href = data.url
    },
    vueRouterNavigate(url) {
      if (url) this.$router.push(url)
    },
    registerEventListeners() {
      EventBus.$on('navigateTo', this.vueRouterNavigate)
      if (this?.CORE?.socket) this.CORE.socket.addEventListener('redirect', this.redirectByEvent)
    },
    unregisterEventListeners() {
      EventBus.$off('navigateTo', this.vueRouterNavigate)
      if (this?.CORE?.socket) this.CORE.socket.removeEventListener('redirect', this.redirectByEvent)
    },
    getEnabledFunctions() {
      return this.$CORE?.enabledFunctions || []
    },
    async storeEnabledFunctions() {
      const enabledFunctions = this.getEnabledFunctions()
      this.$store.dispatch('setEnabledFunctions', enabledFunctions)
    },
    getSingleLayoutRenderingId() {
      return this.$route.query['idLayout']
    },
    renderLayout() {
      this.getLayout().then(async (layout) => {
        if (this.hasContent(layout.js)) await this.setScript(layout.js)

        if (this.hasContent(layout.css)) {
          this.checkUserAgent()
          this.setStyle(layout.css)
        }

        this.setMetas(layout)
        this.storeEnabledFunctions()
        this.layout = layout.html
        //this.$forceUpdate()
      })
    },
  },
  watch: {
    $route: {
      deep: true,
      immediate: true,
      handler: function(newVal, oldVal) {
        if(newVal?.fullPath === oldVal?.fullPath) return
        Object.assign(this.$CORE.params, this.$route.query)
        this.renderLayout()
      },
    },
  },
  async created() {
    this.registerEventListeners()
    // Force reload the component when navigating to the same URL
    document.addEventListener('click', (event) => {
      const clickedElement = event.target.closest('.router-link-exact-active')
      if (clickedElement) {
        this.$forceUpdate()
      }
    })
  },
  beforeDestroy() {
    this.unregisterEventListeners()
  },
}
</script>
